import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  textFieldStyles,
} from "../../../../Styles/Styles";

export default function EditService({ serviceId, setEditingServiceId }) {
  const [service, setService] = useState({
    name: "",
    description: "",
    image: null,
    imagePreview: null,
    parent: "",
  });
  const [loading, setLoading] = useState(false);
  const [parentServices, setParentServices] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch service details to edit
    const fetchServiceDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/singleservice/${serviceId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error fetching service with id: ${serviceId}`);
        }

        const data = await response.json();
        setService({
          name: data.name,
          description: data.description,
          image: null,
          imagePreview: data.image,
          parent: data.parent || "",
        });
      } catch (error) {
        console.error("Error fetching service details:", error);
        setError("Failed to load service details. Please try again.");
      }
    };

    // Fetch parent services
    const fetchParentServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/parentServices`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch parent services.");
        }

        const data = await response.json();
        setParentServices(data || []);
      } catch (error) {
        console.error("Error fetching parent services:", error);
        setError("Failed to load parent services. Please try again.");
      }
    };

    fetchServiceDetails();
    fetchParentServices();
  }, [serviceId]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const preview = file ? URL.createObjectURL(file) : null;

    setService({
      ...service,
      image: file,
      imagePreview: preview,
    });
  };

  const handleSaveService = async () => {
    setLoading(true);
    if (!service.name.trim() || !service.description) {
      setError("Service name and description are required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", service.name.trim());
    formData.append("description", service.description.trim());
    formData.append("parent", service.parent || "null");
    if (service.image) {
      formData.append("image", service.image);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/update/${serviceId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      alert("Service updated successfully!");
      setEditingServiceId(null);
    } catch (error) {
      console.error("Error saving service:", error);
      setError("Failed to update service. Please try again.");
    } finally {
      setLoading(false); // Ensure loading is always reset
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
        Edit Service
      </Typography>
      <TextField
        label="Service Name"
        variant="outlined"
        margin="normal"
        fullWidth
        value={service.name}
        onChange={(e) => setService({ ...service, name: e.target.value })}
        sx={{ ...textFieldStyles }}
      />
      <TextField
        label="Service Description"
        variant="outlined"
        margin="normal"
        fullWidth
        multiline
        minRows={2}
        value={service.description}
        onChange={(e) =>
          setService({ ...service, description: e.target.value })
        }
        sx={{ ...textFieldStyles }}
      />
      <FormControl fullWidth margin="normal">
        <InputLabel sx={{ ...fonts }} id="parent-service-label">
          Parent Service
        </InputLabel>
        <Select
          label="Parent Service"
          labelId="parent-service-label"
          value={service.parent}
          onChange={(e) => setService({ ...service, parent: e.target.value })}
          sx={{ ...fonts }}
        >
          <MenuItem value="" sx={{ ...fonts }}>
            <em>None</em>
          </MenuItem>
          {parentServices.map((parentService) => (
            <MenuItem
              key={parentService._id}
              value={parentService._id}
              sx={{ ...fonts }}
            >
              {parentService.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        accept="image/*"
        onChange={handleFileChange}
        label="Service Image"
        type="file"
        variant="outlined"
        fullWidth
        required
        margin="normal"
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ ...textFieldStyles }}
      />
      {service.imagePreview && (
        <Box sx={{ marginTop: 2 }}>
          <img
            src={service.imagePreview}
            alt="Service Preview"
            style={{ width: "200px" }}
          />
        </Box>
      )}
      <Button
        onClick={handleSaveService}
        className="main-button-style"
        sx={{ mt: 2, ...mainButtonStyles }}
        disabled={loading}
      >
        {loading ? (
          <>
            <span>Saving...</span>
            <CircularProgress size={20} sx={{ color: "white" }} />
          </>
        ) : (
          <>
            <span>Save Changes</span>
            <ArrowForwardOutlinedIcon fontSize="small" />
          </>
        )}
      </Button>
      {error && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
